import { inject, Injectable } from '@angular/core';
import { ExplorerMapService } from '../../explorer-map.service';
import {
  BehaviorSubject,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  take,
} from 'rxjs';
import { LayerSpecification, Map, MapMouseEvent, Popup, SourceSpecification, StyleSpecification } from 'maplibre-gl';
import { Feature, FeatureCollection } from 'geojson';
import { SimulationMasterplan } from '../../../interfaces/simulation/ISimulation';
import { Store } from '@ngrx/store';
import { _selectExplorerSJMap } from '../../../store/selectors/explorer/explorer-sjmap.selector';
import { TLayer, TLayerUUID } from '../../../types/explorer/sjmap2/TLayer';
import { SyncMapsService } from '../../shared/syncMaps.service';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import {
  addAnalyticsData,
  cleanAnalyticsData,
} from '../../../store/actions/explorer/explorer-analytics-tool.actions';
import { AnalyticsDrawStateService } from '../analytics-draw-state/analytics-draw-state.service';
import { AnalyticsToolService } from '../analytics-tool/analytics-tool.service';
import { SimulationDrawClassesService } from '../../simulation/simulation-draw-classes.service';
import { TitilerNewService } from '../../shared/titiler-new.service';
import { RasterTargetResolverService } from '../../shared/raster-target-resolver.service';
import { MapEventSubscriptions } from '../../../utils/map/map-event-subscriptions';

@Injectable({
  providedIn: 'root',
})
export class ExplorerSplitMapService {
  static instance: ExplorerSplitMapService;
  private store: Store = inject(Store);

  private explorerMapService: ExplorerMapService = inject(ExplorerMapService);
  private analyticsToolService: AnalyticsToolService =
    inject(AnalyticsToolService);
  private analyticsDrawState: AnalyticsDrawStateService = inject(
    AnalyticsDrawStateService
  );
  private simulationDrawClassesService: SimulationDrawClassesService = inject(
    SimulationDrawClassesService
  );
  private titilerNewService: TitilerNewService = inject(TitilerNewService);
  private rasterResolver: RasterTargetResolverService = inject(
    RasterTargetResolverService
  );

  /**
   * BehaviourSubject che emette true se la splitMap è aperta, false altrimenti.
   * @type BehaviorSubject\<boolean\>
   */
  public readonly isOpen$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  /**
   * BehaviourSubject che emette la splitMap.
   * @type BehaviorSubject\<Map\>
   */
  public readonly splitMap$: BehaviorSubject<Map> = new BehaviorSubject<Map>(
    null
  );

  /**
   * BehaviourSubject che emette un array di UUID dei layer della SJMap visibili nella splitMap.
   * @type BehaviorSubject\<TLayerUUID[]\>
   */
  public readonly splitMapLayers$: BehaviorSubject<TLayerUUID[]> =
    new BehaviorSubject<TLayerUUID[]>([]);

  /**
   * BehaviourSubject che emette un array di UUID delle simulazioni visibili nella splitMap.
   * @type BehaviorSubject\<string[]\>
   */
  public readonly splitMapSimulations$: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);

  DEFAULT_DRAW_STYLE = this.explorerMapService.DEFAULT_DRAW_STYLE;

  private draw!: MapboxDraw;

  // Stable references, so off() removes exactly what on() registered on every initializeDraw()
  private readonly drawCreateHandler = (e: { features: Feature[] }) => this.onDrawCreate(e);
  private readonly drawDeleteHandler = (e: { features: Feature[] }) => this.onDrawDelete(e);
  private readonly drawUpdateHandler = (e: { features: Feature[] }) => this.onDrawUpdate(e);
  private drawFeatureClickHandler?: (e: MapMouseEvent) => void;
  private readonly splitMapSubs = new MapEventSubscriptions();
  private mainToSplitMirror: globalThis.Map<string, string> =
    new globalThis.Map<string, string>();
  private simulationOriginalVisibility: globalThis.Map<string, string> =
    new globalThis.Map<string, string>();

  isDrawing: BehaviorSubject<{
    status: boolean;
    layerId: string | null;
    analytics: boolean;
  }> = new BehaviorSubject<{
    status: boolean;
    layerId: string | null;
    analytics: boolean;
  }>({
    status: false,
    layerId: null,
    analytics: false,
  });

  private drawCountMap = new globalThis.Map<string, number>([
    ['Point', 0],
    ['Polygon', 0],
    ['LineString', 0],
  ]);

  popup = new Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '450px',
  });

  public splitFeaturePopups: globalThis.Map<string, maplibregl.Popup> =
    new globalThis.Map();

  public lastDrawnFeatures: FeatureCollection | null = null;

  constructor() {
    ExplorerSplitMapService.instance = this;
  }

  /**
   * Salva la splitMap.
   * @param splitMap La mappa secondaria (split map)
   */
  public onSplitMapLoad(splitMap: Map) {
    const currentMap = this.splitMap$.value;

    if (splitMap !== currentMap) {
      splitMap.setMaxZoom(18);
      this.splitMap$.next(splitMap);
      this.initializeDraw(splitMap);
      this.splitMapSubs.track(
        splitMap.on('click', (e) => {
          splitMap.dragPan.enable();
        }),
      );
    }
  }

  public detachSplitMap(splitMap: Map | null | undefined): void {
    if (!splitMap || splitMap !== this.splitMap$.value) return;

    this.splitMapSubs.clear();
    splitMap.off('draw.create', this.drawCreateHandler);
    splitMap.off('draw.delete', this.drawDeleteHandler);
    splitMap.off('draw.update', this.drawUpdateHandler);
    if (this.drawFeatureClickHandler) {
      splitMap.off('click', this.drawFeatureClickHandler);
      this.drawFeatureClickHandler = undefined;
    }

    this.draw = undefined;
    this.splitMap$.next(null);
  }

  initializeDraw(splitMap: Map) {
    if (!splitMap) return;

    // Rimuovi eventuali controlli Draw già presenti (per sicurezza)
    try {
      splitMap.removeControl(this.draw);
    } catch (e) {}

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: this.DEFAULT_DRAW_STYLE,
    });

    splitMap.addControl(this.draw, 'top-right');

    // Rimuovi vecchi event handler (per sicurezza)
    splitMap.off('draw.create', this.drawCreateHandler);
    splitMap.off('draw.delete', this.drawDeleteHandler);
    splitMap.off('draw.update', this.drawUpdateHandler);

    // Aggiungi gli eventi di disegno dopo aver inizializzato il controllo
    splitMap.on('draw.create', this.drawCreateHandler);
    splitMap.on('draw.delete', this.drawDeleteHandler);
    splitMap.on('draw.update', this.drawUpdateHandler);

    // Bind click handler for drawn features
    this.bindDrawFeatureClickPopup(splitMap);
  }

  // ADD THESE NEW METHODS FOR DRAWING FUNCTIONALITY

  /**
   * Enables drawing mode on the split map
   * @param mode Drawing mode ('point', 'line', 'line_string', 'polygon')
   * @param layerId Layer ID to associate with the drawing
   */
  enableDraw(
    mode: 'point' | 'line' | 'line_string' | 'polygon',
    layerId: string = null,
    analytics: boolean = true
  ): void {
    if (!this.draw) {
      // console.warn('Drawing not initialized on split map');
      return;
    }
    this.isDrawing.next({ status: true, layerId, analytics });
    this.draw.changeMode(`draw_${mode}`);
  }

  /**
   * Disables drawing mode on the split map
   */
  disableDraw(): void {
    if (!this.draw) {
      // console.warn('Drawing not initialized on split map');
      return;
    }
    this.draw.changeMode('simple_select');
    this.isDrawing.next({ status: false, layerId: null, analytics: false });
  }

  private isBaseLayer(layerId: string): boolean {
    const baseLayerIds = ['mapbox-xyz', 'basemap-layer']; // Aggiungi gli ID reali della tua mappa
    return baseLayerIds.includes(layerId);
  }

  getRasterLayers(): LayerSpecification[] {
    if (!this.splitMap$.value) {
      console.error('Mappa non inizializzata');
      return [];
    }

    return this.splitMap$.value
      .getStyle()
      ?.layers.filter(
        (layer) => layer?.type === 'raster' && !this.isBaseLayer(layer?.id)
      );
  }

  /**
   * Gets a drawn feature by ID
   * @param featureId The feature ID
   * @returns The drawn feature or null if not found
   */
  getDrawnFeature(featureId: string): Feature | undefined {
    if (!this.draw) return undefined;
    return this.draw.get(featureId);
  }

  /**
   * Deletes a drawn feature by ID
   * @param featureId The feature ID to delete
   */
  deleteDrawnFeature(featureId: string): void {
    if (!this.draw) return;
    this.draw.delete(featureId);
  }

  /**
   * Sets a property on a drawn feature
   * @param drawFeatureId The feature ID
   * @param property The property name
   * @param value The property value
   */
  setDrawFeatureProperty(
    drawFeatureId: string,
    property: string,
    value: unknown
  ): void {
    if (!this.draw) return;
    this.draw.setFeatureProperty(drawFeatureId, property, value);
  }

  /**
   * Generates a readable name for a geometry type
   * @param geometryType The geometry type
   * @returns A readable name
   */
  generateReadableName(geometryType: string): string {
    const currentCount = this.drawCountMap.get(geometryType) ?? 0;
    const newCount = currentCount + 1;
    this.drawCountMap.set(geometryType, newCount);
    return `${geometryType} ${newCount}`;
  }

  bindDrawFeatureClickPopup(splitMap: Map) {
    if (!splitMap) return;
    // Removes the handler registered by a previous initializeDraw()
    if (this.drawFeatureClickHandler) {
      splitMap.off('click', this.drawFeatureClickHandler);
    }
    this.drawFeatureClickHandler = (e: MapMouseEvent) => {
      const currentMode =
        (this.draw as any).getMode?.() || (this.draw as any).getMode;
      if (currentMode && currentMode !== 'simple_select') {
        return;
      }
      const features = splitMap.queryRenderedFeatures(e.point, {
        layers: splitMap
          .getStyle()
          .layers.filter(
            (l) =>
              l.id.startsWith('gl-draw') &&
              !l.id.includes('midpoint') &&
              !l.id.includes('vertex')
          )
          .map((l) => l.id),
      });
      if (!features.length) return;
      const feature = features[0];
      const featureId = feature.properties && feature.properties['id'];
      if (!featureId) return;
      const logicalFeature = this.draw.get(featureId);
      if (!logicalFeature) return;

      this.splitFeaturePopups.set(featureId, this.popup);

      // 👉 Salta popup cestino per feature con "intervention" (simulazioni)
      if (
        logicalFeature.properties &&
        'intervention' in logicalFeature.properties
      ) {
        // Il popup simulation viene gestito dal SimulationDrawClassesService, quindi qui non mostro nulla
        return;
      }
      const mirroredFromMainId = logicalFeature.properties?.['mirrorOf'];
      const canMirror =
        !mirroredFromMainId && this.hasVisibleRasterOnMainMap();
      const isMirrored = this.hasMirroredFeatureForMain(featureId);
      const currentName =
        (logicalFeature.properties && logicalFeature.properties['label']) ||
        this.generateReadableName(logicalFeature.geometry?.type || 'Point');
      const mirrorCheckboxHTML = canMirror
        ? `
          <div class="form-check mb-2" style="color:#FFFFFF">
            <input class="form-check-input" type="checkbox" id="mirror-feature-checkbox" ${
              isMirrored ? 'checked' : ''
            }>
            <label class="form-check-label" style="color:black" for="mirror-feature-checkbox">Mirror to Main Map</label>
          </div>
        `
        : '';
      let popupContent = `
        <div class="popup-actions mt-3">
          <div class="mb-2">
            <label for="feature-name-input" class="form-label" style="color:black; font-weight:600;">Name</label>
            <input class="form-control form-control-sm" type="text" id="feature-name-input" >
            <button class="btn btn-primary btn-sm w-100 mt-2" id="save-feature-name-btn">Save name</button>
          </div>
          ${mirrorCheckboxHTML}
          <button class="btn btn-danger btn-sm w-100 delete-feature-btn"><svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
              <g id="SVGRepo_iconCarrier">
                <path d="M3 6H5H21" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19.21 6L18.2761 19.1422C18.2268 19.8422 17.6397 20.3924 16.9378 20.3924H7.0622C6.36034 20.3924 5.77322 19.8422 5.72386 19.1422L4.79 6H19.21Z" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
            </svg>
            </button>
        </div>
      `;

      this.popup.setLngLat(e.lngLat).setHTML(popupContent).addTo(splitMap);

      setTimeout(() => {
        const popupElement = document.querySelector(
          '.maplibregl-popup-content'
        );
        if (popupElement) {
          popupElement.classList.add('popup-actions');
        }

        const popupEl = this.popup.getElement();
        const checkbox = popupEl.querySelector(
          '#mirror-feature-checkbox'
        ) as HTMLInputElement | null;
        if (checkbox) {
          checkbox.onchange = () => {
            if (!this.draw) return;
            if (checkbox.checked) {
              const mirroredId =
                this.addMirroredFeatureFromMain(logicalFeature);
              if (!mirroredId) {
                checkbox.checked = false;
              }
            } else {
              this.removeMirroredFeatureForMain(featureId);
            }
          };
        }

        const saveBtn = popupEl.querySelector<HTMLElement>(
          '#save-feature-name-btn'
        );
        const nameInput = popupEl.querySelector(
          '#feature-name-input'
        ) as HTMLInputElement | null;
        if (saveBtn && nameInput) {
            nameInput.value = currentName;
          saveBtn.onclick = () => {
            const newName = nameInput.value?.trim() || currentName;
            try {
              this.setDrawFeatureProperty(featureId, 'label', newName);
            } catch {}
            try {
              const {
                updateAnalyticsPointName,
              } = require('../../../store/actions/explorer/explorer-analytics-tool.actions');
              this.store.dispatch(
                updateAnalyticsPointName({ featureId, newName })
              );
            } catch {}
          };
        }

        const btn = popupEl.querySelector('.delete-feature-btn');
        if (btn) {
          btn.addEventListener('click', () => {
            if (mirroredFromMainId) {
              this.explorerMapService.removeMirroredFeatureForSplit(
                mirroredFromMainId
              );
              this.popup.remove();
              return;
            }

            this.draw.delete(featureId);
            this.popup.remove();
            this.store.dispatch(cleanAnalyticsData({ featureId }));
            // Also remove mirrored copy if present
            this.removeMirroredFeatureForMain(featureId);
          });
        }
      }, 0);
    };
    splitMap.on('click', this.drawFeatureClickHandler);
  }

  hasMirroredFeatureForMain(originalId: string): boolean {
    const mainSvc = this.explorerMapService as any;
    const mainDraw = mainSvc?.draw as any;
    const mirroredId = this.mainToSplitMirror.get(originalId);
    if (mirroredId && mainDraw) {
      try {
        const f = mainDraw.get(mirroredId);
        if (f) return true;
        // stale mapping -> drop it
        this.mainToSplitMirror.delete(originalId);
      } catch {}
    }
    // fallback: search by mirrorOf on main draw
    try {
      if (mainDraw?.getAll) {
        const all = mainDraw.getAll().features || [];
        const found = all.find(
          (f: Feature) => f?.properties?.['mirrorOf'] === originalId
        );
        if (found && found.id) {
          this.mainToSplitMirror.set(originalId, found.id);
          return true;
        }
      }
    } catch {}
    return false;
  }

  addMirroredFeatureFromMain(originalFeature: Feature): string | null {
    if (!originalFeature) return null;
    try {
      const mainMapService = this.explorerMapService as any;
      const mainDraw = mainMapService?.draw as any;
      if (!mainDraw) return null;
      const clone: Feature = {
        type: 'Feature',
        properties: {
          ...(originalFeature.properties || {}),
          mirrorOf: originalFeature.id,
        },
        geometry: JSON.parse(JSON.stringify(originalFeature.geometry)),
      };
      const res = mainDraw.add(clone);
      const newId = Array.isArray(res) ? res[0] : res;
      if (typeof newId === 'string') {
        this.mainToSplitMirror.set(originalFeature.id as string, newId);
        // Dispatch analytics on main map for mirrored point
        (async () => {
          const mainMap = mainMapService.getMap?.();
          const style = mainMap?.getStyle?.();
          const layers = (style?.layers || []).slice().reverse();
          let targetLayerUUID: string | null = null;
          for (const layer of layers) {
            if (layer.type !== 'raster') continue;
            const id = layer.id;
            if (!id || id === 'mapbox-xyz' || id === 'basemap-layer') continue;
            const visibility = mainMap.getLayoutProperty(id, 'visibility');
            if (visibility === 'none') continue;
            targetLayerUUID = await this.maplibreRasterLayerIdToSJMapUUID(id);
            if (targetLayerUUID) break;
          }
          if (targetLayerUUID) {
            this.store.dispatch(
              addAnalyticsData({ layerUUID: targetLayerUUID, name: newId })
            );
          }
        })().catch(() => {});
        return newId;
      }
      return null;
    } catch {
      return null;
    }
  }

  removeMirroredFeatureForMain(originalId: string): void {
    const mainMapService = this.explorerMapService as any;
    const mainDraw = mainMapService?.draw as any;
    const mirroredId = this.mainToSplitMirror.get(originalId);
    if (mainDraw && mirroredId) {
      try {
        mainDraw.delete(mirroredId);

        const popup = mainMapService.mainMapFeaturePopups.get(mirroredId);
        if (popup) {
          popup.remove();
          mainMapService.mainMapFeaturePopups.delete(mirroredId);
        }

        // Dispatch clean analytics for mirrored feature on main map
        this.store.dispatch(cleanAnalyticsData({ featureId: mirroredId }));
      } catch {}
      this.mainToSplitMirror.delete(originalId);
    }
  }

  private async maplibreRasterLayerIdToSJMapUUID(
    layerId: string
  ): Promise<string | null> {
    return new Promise((resolve) => {
      this.store
        .select(_selectExplorerSJMap)
        .pipe(take(1))
        .subscribe((sjmapState) => {
          const allLayers = sjmapState?.layers || ([] as any[]);
          for (const layer of allLayers) {
            if (layer?.type !== 'raster') continue;
            const sources = (layer as any).sources || [];
            for (const src of sources) {
              const ml = src?.maplibre;
              if (ml && typeof ml.layer === 'string' && ml.layer === layerId) {
                resolve(layer.uuid);
                return;
              }
            }
          }
          resolve(null);
        });
    });
  }

  async resolveTopVisibleRasterUUID(): Promise<string | null> {
    return this.rasterResolver.resolveTargetRasterUUID({
      map: this.splitMap$.value,
      allowedUUIDs: this.splitMapLayers$.value,
      isBaseLayer: (id) => id === 'mapbox-xyz' || id === 'basemap-layer',
      maplibreIdToUUID: this.maplibreRasterLayerIdToSJMapUUID.bind(this),
    });
  }

  async onDrawCreate(e: { features: Feature[] }): Promise<void> {
    const feature = e.features[0];
    const featureId: string = feature.id as string;

    if (!feature.properties?.['label']) {
      const geometryType = feature.geometry.type;
      const label = this.generateReadableName(geometryType);
      this.draw.setFeatureProperty(featureId, 'label', label);
    }

    if (this.isDrawing.value.analytics) {
      const targetLayerUUID =
        (await this.rasterResolver.resolveTargetRasterUUID({
          map: this.splitMap$.value,
          geometry: feature.geometry,
          allowedUUIDs: this.splitMapLayers$.value,
          isBaseLayer: (id) => id === 'mapbox-xyz' || id === 'basemap-layer',
          maplibreIdToUUID: this.maplibreRasterLayerIdToSJMapUUID.bind(this),
        })) || this.isDrawing.value.layerId;

      if (targetLayerUUID) {
        this.analyticsToolService.changeActiveLayerUUIDTab(targetLayerUUID);
        this.store.dispatch(
          addAnalyticsData({ layerUUID: targetLayerUUID, name: featureId })
        );
      }
    }

    // Se la modalità Point Draw è ancora attiva, riattiva il draw dopo ogni punto
    if (this.analyticsDrawState.isSplitPointActive()) {
      setTimeout(async () => {
        const dynamicUUID = await this.resolveTopVisibleRasterUUID();
        const targetLayerUUID = dynamicUUID || this.isDrawing.value.layerId;
        if (targetLayerUUID) this.enableDraw('point', targetLayerUUID, true);
        else
          this.isDrawing.next({
            status: false,
            layerId: null,
            analytics: false,
          });
      }, 0);
    } else if (this.analyticsDrawState.isSplitPolygonActive()) {
      setTimeout(async () => {
        const dynamicUUID = await this.resolveTopVisibleRasterUUID();
        const targetLayerUUID = dynamicUUID || this.isDrawing.value.layerId;
        if (targetLayerUUID) this.enableDraw('polygon', targetLayerUUID, true);
        else
          this.isDrawing.next({
            status: false,
            layerId: null,
            analytics: false,
          });
      }, 0);
    } else {
      this.isDrawing.next({ status: false, layerId: null, analytics: false });
    }
  }

  onDrawDelete(e: { features: Feature[] }): void {}

  /**
   * Handle feature move/resize (update)
   */
  async onDrawUpdate(e: { features: Feature[] }): Promise<void> {
    if (!e.features?.length) return;
    for (const feature of e.features) {
      await this.recalcAnalyticsForFeature(feature.id as string);
    }
  }

  /**
   * Apre o chiude la splitMap in base al suo stato attuale.
   */
  public toggleSplitMap() {
    this.isOpen$.value ? this.closeSplitMap() : this.openSplitMap();
  }

  public openSplitMap() {
    this.isOpen$.next(true);
  }

  public closeSplitMap(): void {
    // Prima del teardown, riporta sulla main map tutti i masterplan
    // attualmente assegnati alla split map. La copia evita di iterare
    // sullo stesso array modificato da removeSimulationFromSplitMap().
    const simulationsToRestore = [...this.splitMapSimulations$.value];
    simulationsToRestore.forEach((simulationUUID) => {
      this.removeSimulationFromSplitMap(simulationUUID);
    });

    const splitMap = this.splitMap$.value;
    const layers =
      splitMap?.getStyle()?.layers?.map((layer) => layer.id).reverse() || [];

    layers.forEach((layerId) => {
      try {
        if (splitMap.getLayer(layerId)) {
          splitMap.removeLayer(layerId);
        }
      } catch (e) {}
    });

    // Pulizia difensiva: a split map chiusa nessun masterplan deve
    // risultare ancora assegnato alla colonna destra.
    this.splitMapSimulations$.next([]);
    this.simulationOriginalVisibility.clear();

    this.explorerMapService.clearAllKpiRastersOnSplitClose();
    this.isOpen$.next(false);
  }

  /**
   * Aggiunge un layer della SJMap alla splitMap.
   * @param SJMLayerUUID UUID del layer da aggiungere alla splitMap
   */
  public addSplitMapLayer(SJMLayerUUID: TLayerUUID) {
    if (!this.splitMapLayers$.value.includes(SJMLayerUUID))
      this.splitMapLayers$.next([...this.splitMapLayers$.value, SJMLayerUUID]);
  }

  /**
   * Rimuove un layer della SJMap dalla splitMap.
   * @param SJMLayerUUID UUID del layer da rimuovere dalla splitMap
   */
  public removeSplitMapLayer(SJMLayerUUID: TLayerUUID) {
    this.splitMapLayers$.next(
      this.splitMapLayers$.value.filter((uuid) => uuid !== SJMLayerUUID)
    );
  }

  /**
   * Aggiunge una simulazione alla splitMap spostando i layer dalla mappa principale.
   * @param simulationUUID UUID della simulazione da aggiungere alla splitMap
   */
  public addSimulationToSplitMap(simulationUUID: string) {
  if (!this.splitMapSimulations$.value.includes(simulationUUID)) {
    this.splitMapSimulations$.next([
      ...this.splitMapSimulations$.value,
      simulationUUID,
    ]);
  }

  const mainMap = this.explorerMapService.getMap();
  const splitMap = this.splitMap$.value;
  if (!mainMap || !splitMap) return;

  const layerId = `sim-${simulationUUID}`;
  const sourceId = `sim-src-${simulationUUID}`;

  // Verifica se il layer esiste nella mappa principale
  if (mainMap.getLayer(layerId) && mainMap.getSource(sourceId)) {
    const layers = [layerId, `${layerId}-line`, `${layerId}-point`];
    // memorizzo la visibilità attuale per ogni layer
    const visibilityPerLayer: Record<string, 'visible' | 'none'> = {};
    // Prima nasconde i layer sulla mappa principale e traccia la visibilità originale
    layers.forEach((id) => {
  if (mainMap.getLayer(id)) {
    const currentVisibility =
      mainMap.getLayoutProperty(id, 'visibility') as 'visible' | 'none' | undefined;
    // normalizzo: se non è 'visible', la tratto come 'none'
    const normalizedVisibility: 'visible' | 'none' =
      currentVisibility === 'visible' ? 'visible' : 'none';

    this.simulationOriginalVisibility.set(id, normalizedVisibility);
    visibilityPerLayer[id] = normalizedVisibility;
    mainMap.setLayoutProperty(id, 'visibility', 'none');
    }
  });

    // Verifica se la split map è pronta
    if (!splitMap.isStyleLoaded?.()) {
      const onceLoaded = () => {
        splitMap.off('load', onceLoaded);
        this.addSimulationToSplitMap(simulationUUID);
      };
      splitMap.on('load', onceLoaded);
      return;
    }

    const source = mainMap.getSource(sourceId);
    if (source && source.type === 'vector') {
      const sourceData = source.serialize();
      if (!splitMap.getSource(sourceId)) {
        splitMap.addSource(sourceId, sourceData);
      }

      const addLayersToSplitMap = () => {
        layers.forEach((id) => {
          if (mainMap.getLayer(id) && !splitMap.getLayer(id)) {
            const layerData = mainMap.getLayer(id).serialize();
            layerData.layout = layerData.layout || {};
            // forzavamo sempre 'visible' (stamm nguaiat)
            // layerData.layout.visibility = 'visible';
            // adesso uso la visibilità che aveva sulla mainMap
            const vis = visibilityPerLayer[id];
            layerData.layout = layerData.layout || {};
            layerData.layout.visibility = vis;
            splitMap.addLayer(layerData);
          }
        });
      };

      if (splitMap.getSource(sourceId)) {
        addLayersToSplitMap();
        this.bindSimulationPopupOnSplitMap(simulationUUID);
      } else {
        const checkSource = () => {
          if (splitMap.getSource(sourceId)) {
            addLayersToSplitMap();
            this.bindSimulationPopupOnSplitMap(simulationUUID);
          } else {
            setTimeout(checkSource, 100);
          }
        };
        checkSource();
      }
    }
  }
}

  /**
   * Configura i popup per una simulazione sulla split map.
   * @param simulationUUID UUID della simulazione per cui configurare i popup
   */
  private bindSimulationPopupOnSplitMap(simulationUUID: string) {
    const layerId = `sim-${simulationUUID}`;
    // Crea un oggetto simulazione temporaneo per il binding
    const tempSimulation = { uuid: simulationUUID } as SimulationMasterplan;
    this.simulationDrawClassesService.bindSimulationPopupOnSplitMapLayers(
      tempSimulation,
      layerId
    );
  }

  /**
   * Rimuove una simulazione dalla splitMap e la riporta sulla mappa principale.
   * @param simulationUUID UUID della simulazione da rimuovere dalla splitMap
   */
  public removeSimulationFromSplitMap(simulationUUID: string): void {
    this.splitMapSimulations$.next(
      this.splitMapSimulations$.value.filter(
        (uuid) => uuid !== simulationUUID
      )
    );

    const mainMap = this.explorerMapService.getMap();
    const splitMap = this.splitMap$.value;
    const layerId = `sim-${simulationUUID}`;
    const sourceId = `sim-src-${simulationUUID}`;
    const layers = [layerId, `${layerId}-line`, `${layerId}-point`];

    // Rimuove listener, layer e source dalla split map, se e ancora disponibile.
    this.simulationDrawClassesService.unbindSimulationPopupOnSplitMapLayers(layerId);
    if (splitMap) {
      layers.forEach((id) => {
        if (splitMap.getLayer(id)) {
          splitMap.removeLayer(id);
        }
      });

      if (splitMap.getSource(sourceId)) {
        splitMap.removeSource(sourceId);
      }
    }

    // Il ripristino della main map non deve dipendere dalla presenza
    // della split map, che potrebbe essere gia in fase di teardown.
    if (!mainMap) return;

    this.store
      .select(_selectExplorerSJMap)
      .pipe(take(1))
      .subscribe((sjmapState) => {
        const masterplan = sjmapState?.masterplans?.find(
          (item) => item.uuid === simulationUUID
        );
        const visibility: 'visible' | 'none' =
          masterplan?.isVisible === false ? 'none' : 'visible';

        layers.forEach((id) => {
          if (mainMap.getLayer(id)) {
            mainMap.setLayoutProperty(id, 'visibility', visibility);
          }
          this.simulationOriginalVisibility.delete(id);
        });
      });
  }

  public resetSplitMapStyle() {
    const mainMap = this.explorerMapService.getMap();
    const splitMap = this.splitMap$.value;
    if (!mainMap || !splitMap) return;

    if (!splitMap.isStyleLoaded?.()) {
      const onceLoaded = () => {
        splitMap.off('load', onceLoaded);
        this.resetSplitMapStyle();
      };
      splitMap.on('load', onceLoaded);
      return;
    }

    const currentSplitStyle = splitMap.getStyle();
    const kpiSourceSnapshot: Record<string, SourceSpecification> = {};
    const kpiLayersSnapshot: LayerSpecification[] = [];

    if (currentSplitStyle) {
      Object.entries(currentSplitStyle.sources || {}).forEach(
        ([sourceId, sourceSpec]) => {
          if (sourceId.startsWith('kpi-')) {
            kpiSourceSnapshot[sourceId] = sourceSpec;
          }
        }
      );
      (currentSplitStyle.layers || []).forEach((layer: LayerSpecification) => {
        if (layer.id && layer.id.startsWith('kpi-')) {
          kpiLayersSnapshot.push(layer);
        }
      });
    }

    // Salva sempre le feature disegnate PRIMA del reset
    if (this.draw) {
      try {
        const features = this.draw.getAll();
        if (features?.type === 'FeatureCollection') {
          this.lastDrawnFeatures = features;
        }
      } catch (e) {
        this.lastDrawnFeatures = null;
      }
    }
    // Rimuovi il controllo Draw se presente
    if (this.draw) {
      try {
        splitMap.removeControl(this.draw);
      } catch (e) {}
      this.draw = undefined;
    }

    // Rimuovi manualmente tutti i layer di draw (controlla se l'id contiene 'gl-draw')
    const style = splitMap.getStyle();
    const layers = style?.layers?.map((l) => l.id) || [];
    layers.forEach((layerId) => {
      if (layerId.includes('gl-draw') && splitMap.getLayer(layerId)) {
        try {
          splitMap.removeLayer(layerId);
        } catch (e) {}
      }
    });
    // Rimuovi manualmente tutte le sorgenti draw (mapbox-gl-draw-cold/hot)
    const sources = Object.keys(style?.sources || {});
    sources.forEach((sourceId) => {
      if (
        sourceId.includes('gl-draw') ||
        sourceId === 'mapbox-gl-draw-cold' ||
        sourceId === 'mapbox-gl-draw-hot'
      ) {
        if (splitMap.getSource(sourceId)) {
          try {
            splitMap.removeSource(sourceId);
          } catch (e) {}
        }
      }
    });

    // Ripristina tutte le simulazioni sulla mappa principale prima del reset
    this.splitMapSimulations$.value.forEach((simulationUUID) => {
      const layerId = `sim-${simulationUUID}`;
      const layers = [layerId, `${layerId}-line`, `${layerId}-point`];
      layers.forEach((id) => {
        if (mainMap.getLayer(id)) {
          const originalVisibility =
            this.simulationOriginalVisibility.get(id) || 'visible';
          mainMap.setLayoutProperty(id, 'visibility', originalVisibility);
          this.simulationOriginalVisibility.delete(id);
        }
      });
    });

    // Svuota la lista delle simulazioni sulla split map
    this.splitMapSimulations$.next([]);
    this.simulationOriginalVisibility.clear();

    // Clona lo style della mainmap escludendo i layer/sorgenti di draw e delle simulazioni (sim-*)
    const mainMapStyle = mainMap.getStyle();
    if (!mainMapStyle || typeof mainMapStyle !== 'object') {
      // console.warn('[resetSplitMapStyle] Invalid main map style:', mainMapStyle);
      return; // Exit early if style is bad
    }

    let styleClone: StyleSpecification;
    try {
      styleClone = JSON.parse(JSON.stringify(mainMapStyle));
    } catch (e) {
      // console.warn('[resetSplitMapStyle] Failed to clone style:', e);
      return;
    }
    styleClone.layers = styleClone.layers.filter((l: LayerSpecification) => {
      const id = l.id as string;
      if (!id) return false;
      if (id.includes('gl-draw')) return false;
      // Evita di copiare i layer delle simulazioni nella splitmap
      if (id === 'sim' || id.startsWith('sim-')) return false;
      if (id.startsWith('sim-src-')) return false;
      if (id.endsWith('-line') && id.startsWith('sim-')) return false;
      if (id.endsWith('-point') && id.startsWith('sim-')) return false;
      if (id.startsWith('kpi-')) return false;
      return true;
    });
    Object.keys(styleClone.sources).forEach((sourceId) => {
      if (
        sourceId.includes('gl-draw') ||
        sourceId === 'mapbox-gl-draw-cold' ||
        sourceId === 'mapbox-gl-draw-hot' ||
        sourceId.startsWith('sim-src-') ||
        sourceId.startsWith('kpi-')
      ) {
        delete styleClone.sources[sourceId];
      }
    });

    splitMap.setStyle(styleClone, { diff: true });

    // Dopo il reset, reinizializza il controllo di disegno sulla splitmap SOLO se non già presente e solo quando la mappa è pronta
    const onStyleData = () => {
      const drawSources = Object.keys(
        splitMap.getStyle()?.sources || {}
      ).filter((id) => id.includes('gl-draw'));

      if (drawSources.length === 0) {
        this.initializeDraw(splitMap);

        // 2. Dopo aver reinizializzato Draw, ripristina le feature SOLO se esistono
        if (
          this.lastDrawnFeatures &&
          this.lastDrawnFeatures.features?.length > 0
        ) {
          try {
            this.draw.add(this.lastDrawnFeatures);
          } catch (e) {
            // console.warn('[Draw] add() failed during reset:', e);
          }
        }
      }
      try {
        Object.entries(kpiSourceSnapshot).forEach(([sourceId, sourceSpec]) => {
          if (!splitMap.getSource(sourceId)) {
            splitMap.addSource(sourceId, sourceSpec);
          }
        });
        kpiLayersSnapshot.forEach((layer: LayerSpecification) => {
          if (!splitMap.getLayer(layer.id)) {
            const layerSource = 'source' in layer ? layer.source : undefined;
            if (!layerSource || splitMap.getSource(layerSource)) {
              splitMap.addLayer(layer);
            }
          }
        });
      } catch (e) {}

      splitMap.off('styledata', onStyleData);
    };

    splitMap.on('styledata', onStyleData);
  }

  private hasVisibleRasterOnMainMap(): boolean {
    try {
      const mainMap = this.explorerMapService.getMap();
      if (!mainMap) return false;

      const layers = mainMap.getStyle()?.layers || [];
      return layers.some(
        (l) =>
          l.type === 'raster' &&
          l.id &&
          l.id !== 'basemap-layer' &&
          l.id !== 'mapbox-xyz' &&
          !l.id.startsWith('background') &&
          !l.id.startsWith('base-') &&
          mainMap.getLayoutProperty(l.id, 'visibility') !== 'none'
      );
    } catch (e) {
      console.warn('Could not check for raster layers on main map.', e);
      return false;
    }
  }

  /**
   * Shared method: recalc analytics whenever feature is created or updated
   */
  private async recalcAnalyticsForFeature(featureId: string) {
    const f = this.draw.get(featureId);
    const targetLayerUUID =
      (await this.rasterResolver.resolveTargetRasterUUID({
        map: this.splitMap$.value,
        geometry: f?.geometry ?? null,
        allowedUUIDs: this.splitMapLayers$.value,
        isBaseLayer: (id) => id === 'mapbox-xyz' || id === 'basemap-layer',
        maplibreIdToUUID: this.maplibreRasterLayerIdToSJMapUUID.bind(this),
      })) || this.isDrawing.value.layerId;

    if (targetLayerUUID) {
      this.analyticsToolService.changeActiveLayerUUIDTab(targetLayerUUID);
      this.store.dispatch(cleanAnalyticsData({ featureId }));
      this.store.dispatch(
        addAnalyticsData({ layerUUID: targetLayerUUID, name: featureId })
      );
    }
  }
}
